// Доступы площадки. Панель, а не модалка: набор доступов заменяется сколько угодно раз, и
// ничего необратимого в замене нет (правило 4).
//
// Наборов на записи ДВА, и они не взаимозаменяемы (тз 6.1): переписка с соискателями работает
// ТОЛЬКО ключом компании, вакансии ведутся ключом сотрудника, потому что пакет размещения на
// площадке привязан к сотруднику. Поэтому вид набора выбирается явно, а не подставляется за
// человека: положенный не туда ключ даёт не поломку, а тишину — помощник просто перестаёт
// отвечать, и причину ищут неделю.
//
// Секрет уходит зашифрованным и обратно не возвращается никогда. Это не наша скрытность: в
// ответе сервера его просто нет, и показать его негде. Сказать это надо прямо в форме — иначе
// человек ищет глазами поле «показать текущий секрет», которого не будет.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiKey2Line, RiLockPasswordLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import type { CredentialOut, KeyKind } from '../api/platform';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useAccountsQuery, useSetCredentialsMutation } from '../queries';
import { MANAGE_OWNER, MANAGE_PERMISSION } from '../views/AccountsList';

/** Что делается этим набором. Название набора без этой фразы не отвечает «а зачем он». */
const ЧТО_ДЕЛАЕТ: Readonly<Record<KeyKind, string>> = {
  company:
    'Ключом компании работает переписка с соискателями: и приём входящих сообщений, и ответы помощника. Вакансии им тоже размещаются.',
  employee:
    'Ключом сотрудника ведутся вакансии: пакет размещения на площадке привязан к сотруднику, а не к компании. Переписка им не работает.',
};

/** Запасные подписи, пока запись не приехала: обычно названия наборов даёт сервер. */
const ЗАПАСНОЕ_ИМЯ: Readonly<Record<KeyKind, string>> = {
  company: 'Ключ компании',
  employee: 'Ключ сотрудника',
};

const ВИДЫ: readonly KeyKind[] = ['company', 'employee'];

function этоВидКлюча(значение: string): значение is KeyKind {
  return значение === 'company' || значение === 'employee';
}

type Поле = 'вид' | 'идентификатор' | 'секрет';

export function CredentialsPanel({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const allowed = useCan(MANAGE_PERMISSION);
  const accounts = useAccountsQuery();
  const mutation = useSetCredentialsMutation(accountId);

  const account = accounts.data?.items.find((item) => item.id === accountId);

  const [вид, setВид] = useState<string>(NO_VALUE);
  const [идентификатор, setИдентификатор] = useState('');
  const [пароль, setПароль] = useState('');
  const [ошибки, setОшибки] = useState<Partial<Record<Поле, string>>>({});
  const [отказ, setОтказ] = useState<string | null>(null);

  // Названия наборов берутся у сервера (`kind_title`): своя копия названия рядом с присланным
  // расходится с ним молча, и через месяц в двух местах системы ключ зовётся по-разному.
  const варианты = ВИДЫ.map((kind) => ({
    value: kind,
    label:
      account?.credentials.find((набор) => набор.kind === kind)?.kind_title ?? ЗАПАСНОЕ_ИМЯ[kind],
  }));

  const выбран = этоВидКлюча(вид) ? вид : undefined;
  const заведён =
    выбран !== undefined &&
    account?.credentials.find((набор) => набор.kind === выбран)?.configured === true;

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(`Заводить доступы площадки может администратор. ${MANAGE_OWNER}`);
      return;
    }
    if (mutation.isPending) {
      setОтказ('Доступы уже сохраняются, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: Partial<Record<Поле, string>> = {};
    if (выбран === undefined) {
      найдено.вид = 'Выберите набор: от этого зависит, что записью можно будет делать.';
    }
    if (идентификатор.trim() === '') {
      найдено.идентификатор =
        'Введите идентификатор доступа — площадка выдаёт его в паре с секретом.';
    }
    if (пароль === '') {
      найдено.секрет = 'Введите секрет целиком: прежний не подставляется, его негде взять.';
    }
    if (Object.keys(найдено).length > 0) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setОтказ(null);
    mutation.mutate(
      {
        kind: выбран as KeyKind,
        client_id: идентификатор.trim(),
        client_secret: пароль,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && <NoPermissionNote что='заводить доступы площадки' кто={MANAGE_OWNER} />}

      {/* Что на записи уже стоит. Без этой строки человек не отличает «кладу первый набор» от
          «затираю рабочий», а посмотреть прежний секрет после замены будет негде. */}
      {account !== undefined && (
        <p className='text-paragraph-xs text-text-sub-600'>
          {состояниеНаборов(account.credentials)}
        </p>
      )}

      <SelectField
        id='credentials-kind'
        label='Какой набор кладём'
        required
        value={вид}
        onChange={setВид}
        options={варианты}
        placeholder='Выберите набор доступов'
        pending={accounts.isPending}
        error={ошибки.вид}
        hint={
          выбран === undefined
            ? 'Наборы не взаимозаменяемы: переписка идёт только ключом компании, вакансии — ключом сотрудника.'
            : ЧТО_ДЕЛАЕТ[выбран]
        }
      />

      <TextField
        id='credentials-client-id'
        label='Идентификатор доступа'
        required
        value={идентификатор}
        onChange={setИдентификатор}
        placeholder='Выдаётся площадкой в настройках приложения'
        icon={RiKey2Line}
        autoComplete='off'
        maxLength={255}
        error={ошибки.идентификатор}
      />

      <TextField
        id='credentials-client-secret'
        label='Секрет доступа'
        required
        value={пароль}
        onChange={setПароль}
        type='password'
        placeholder={заведён ? 'Введите новый секрет целиком' : 'Секрет из настроек площадки'}
        icon={RiLockPasswordLine}
        autoComplete='off'
        maxLength={512}
        error={ошибки.секрет}
      />

      <Hint.Root>
        Секрет уходит на сервер зашифрованным и обратно не возвращается никогда: посмотреть его
        нельзя ни здесь, ни в карточке записи. Заменить — можно в любой момент, этой же формой.
      </Hint.Root>

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending
            ? 'Сохраняем доступы…'
            : заведён
              ? 'Заменить доступы'
              : 'Сохранить доступы'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/**
 * Что на записи уже стоит. Три разных факта — оба набора, один, ни одного — и все три обязаны
 * читаться по-разному (правило 7): «доступов нет» и «ключ компании есть, а сотрудника нет»
 * ведут человека к разным следующим действиям.
 */
function состояниеНаборов(наборы: readonly CredentialOut[]): string {
  const заведены = наборы.filter((набор) => набор.configured);
  if (заведены.length === 0) {
    return 'На этой записи пока не заведено ни одного набора: она не умеет ни размещать вакансии, ни вести переписку.';
  }
  if (заведены.length === наборы.length) {
    return 'На этой записи заведены оба набора. Новый секрет затрёт прежний, и взять прежний будет неоткуда.';
  }
  const имя = заведены[0];
  return `На этой записи заведён только ${(имя?.kind_title ?? ЗАПАСНОЕ_ИМЯ[имя?.kind ?? 'company']).toLowerCase()}. Второй набор пока пуст.`;
}
